import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '../cx';
import styles from './Tabs.module.css';

export interface TabItem {
  value: string;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  'aria-label': string;
  /**
   * `default`: neutral selected text + 3px indicator, counts in pills.
   * `underline`: accent-coloured selected text + 2px underline, counts inline
   * in parentheses — the compact strip above a dense table (dashboard).
   */
  variant?: 'default' | 'underline';
}

/**
 * A controlled tablist with roving tabindex: Arrow Left/Right (and Home/End)
 * move focus and selection together, matching the WAI-ARIA "automatic
 * activation" tabs pattern. The panel itself is the caller's responsibility —
 * this only renders the tab strip.
 */
export function Tabs({ items, value, onChange, className, 'aria-label': ariaLabel, variant = 'default' }: TabsProps) {
  const idBase = useId();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const enabled = items.filter((i) => !i.disabled);
  // When no tab matches (e.g. a filter set elsewhere), keep the strip
  // reachable by Tab: the first enabled tab takes the roving tabindex.
  const hasSelection = enabled.some((i) => i.value === value);
  const tabStop = hasSelection ? value : enabled[0]?.value;

  function focusAndSelect(target: TabItem | undefined): void {
    if (!target) return;
    onChange(target.value);
    refs.current[target.value]?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const currentIndex = enabled.findIndex((i) => i.value === tabStop);
    if (currentIndex === -1) return;
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusAndSelect(enabled[(currentIndex + 1) % enabled.length]);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusAndSelect(enabled[(currentIndex - 1 + enabled.length) % enabled.length]);
        break;
      case 'Home':
        e.preventDefault();
        focusAndSelect(enabled[0]);
        break;
      case 'End':
        e.preventDefault();
        focusAndSelect(enabled[enabled.length - 1]);
        break;
      default:
        break;
    }
  }

  return (
    <div role="tablist" aria-label={ariaLabel} className={cx(styles.tablist, variant === 'underline' && styles.underline, className)} onKeyDown={onKeyDown}>
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[item.value] = el;
            }}
            id={`${idBase}-tab-${item.value}`}
            role="tab"
            type="button"
            aria-selected={selected}
            tabIndex={item.value === tabStop ? 0 : -1}
            disabled={item.disabled}
            className={cx(styles.tab, selected && styles.selected)}
            onClick={() => onChange(item.value)}
          >
            {item.label}
            {item.count !== undefined &&
              (variant === 'underline' ? (
                <span className={styles.countInline}>({item.count})</span>
              ) : (
                <span className={styles.count}>{item.count}</span>
              ))}
          </button>
        );
      })}
    </div>
  );
}
